import { gql, useQuery } from '@apollo/client';
import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitterSquare } from '@fortawesome/free-brands-svg-icons/faTwitterSquare';
import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { Link, useNavigate, useMatch } from 'react-router-dom';
import cx from 'classnames';
import { useAmplitude } from '../amplitude/amplitudeContext';
import Agent from '../models/Agent';
import styles from './AgentProfile.module.scss';
import Button from '../components/Button';
import { useSerializedState } from '../hooks/useSerialized';
import { Skeleton } from '../components/Skeleton';
import Image from '../components/Image';

export const getAgentById = gql`
  query GetAgentById($id: ID!) {
    agent(id: $id) {
      activeLocations {
        city
        state
      }
      bio
      asab
      cityState
      email
      firstName
      languages
      lastName
      phoneNumber
      photo
      preferredName
      website
      twitter
      facebook
      linkedIn
      specializations
      state
      stateBroker
      title
    }
  }
`;

const AgentProfile: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [id, setId] = useState('');
  const match = useMatch('/agent/:nameId');
  useEffect(() => {
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;
    const matches = match?.params?.nameId?.match(uuidRegex) || [];
    if (!matches[0]) {
      return navigate('/');
    }

    setId(matches[0]);
  }, [match, navigate]);

  const { loading, data } = useQuery<{
    agent: Agent;
  }>(getAgentById, { variables: { id } });
  const [agent, setAgent] = useSerializedState(Agent, data?.agent);
  const rawAgent = data?.agent;
  useEffect(() => {
    setAgent(rawAgent);
  }, [rawAgent, setAgent]);

  const { logAmplitudeEvent } = useAmplitude();

  useEffect(() => {
    const defaultTitle = document.title;
    if (agent?.fullName) {
      document.title = `${agent?.fullName} - ${agent?.cityState} Real Estate Agent | eXp Realty®`;
    }
    return () => {
      document.title = defaultTitle;
    };
  }, [agent]);

  const back = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      navigate(-1);
    },
    [navigate]
  );

  const skeleton = loading || !agent;

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.content}>
          <p>eXp {t('Agent Directory')}</p>
          <p>/</p>
          <Link
            to="/"
            onClick={(e) => {
              back(e);
              logAmplitudeEvent('click on return to search');
            }}
            data-test="back"
          >
            {t('Return to Search')}
          </Link>
        </div>
      </div>

      <div className={styles.main}>
        <h1 className={styles.name} data-test="name">
          {skeleton ? <Skeleton width="40rem" /> : agent?.fullName}
        </h1>
        <h2 className={styles.title} data-test="title">
          {skeleton ? <Skeleton width="25rem" /> : agent?.title}
        </h2>
        <div className={styles.layout}>
          <div data-test="info-container" className={styles.info}>
            <Image alt={`${agent?.fullName} - ${agent?.cityState} Real Estate Agent`} className={styles.img} fallBack={Skeleton} src={agent?.photo} />
            {skeleton ? (
              <Skeleton className={styles.button} count={2} />
            ) : (
              <Button
                data-test="join-button"
                onClick={(e) =>
                  logAmplitudeEvent('click on eXp realty join button', {
                    agentFullName: agent?.fullName,
                    agentEmail: agent?.email,
                  })
                }
                className={styles.button}
                href="https://exprealty.com/join"
              >
                {t('Join eXp Realty')}
              </Button>
            )}
            <hr />
            <strong>{t('Contact Info')}</strong>
            {skeleton && <Skeleton count={3} />}
            {agent?.phoneNumber && (
              <a
                className={styles.sub}
                href={`tel:${agent.phoneNumber}`}
                onClick={(e) => {
                  logAmplitudeEvent('click on call agent button', {
                    hasBio: !!agent?.bio,
                  });
                }}
                data-test="info-phone"
              >
                {agent.phoneNumber}
              </a>
            )}
            {agent?.email && (
              <a
                className={styles.sub}
                href={`mailto:${agent.email}`}
                onClick={(e) => {
                  logAmplitudeEvent('click on email agent button', {
                    hasBio: !!agent?.bio,
                  });
                }}
                data-test="info-email"
              >
                {agent?.email}
              </a>
            )}
            {agent?.website && (
              <a
                className={styles.url}
                onClick={(e) => logAmplitudeEvent('click on agent website link')}
                target=" _blank"
                rel="noopener noreferrer"
                href={agent.website.startsWith('http') ? agent.website : `http://${agent.website}`}
                data-test="info-website"
              >
                {agent.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {agent?.facebookUrl ? (
              <a target="_blank" onClick={(e) => logAmplitudeEvent('click on Facebook link')} data-test="agent-facebook" rel="noopener noreferrer" href={agent.facebookUrl}>
                <FontAwesomeIcon className={cx(styles.icon, styles.facebookIcon)} icon={faFacebookSquare} />
              </a>
            ) : null}
            {agent?.linkedInUrl ? (
              <a target="_blank" onClick={(e) => logAmplitudeEvent('click on LinkedIn link')} data-test="agent-linkedin" rel="noopener noreferrer" href={agent.linkedInUrl}>
                <FontAwesomeIcon className={cx(styles.icon, styles.linkedinIcon)} icon={faLinkedin} />
              </a>
            ) : null}
            {agent?.twitterUrl ? (
              <a target="_blank" onClick={(e) => logAmplitudeEvent('click on Twitter link')} data-test="agent-twitter" rel="noopener noreferrer" href={agent.twitterUrl}>
                <FontAwesomeIcon className={cx(styles.icon, styles.twitterIcon)} icon={faTwitterSquare} />
              </a>
            ) : null}
            {skeleton && <Skeleton count={2} />}
            {agent?.languages && agent?.languages.length > 0 && (
              <>
                <strong>{t('Languages')}</strong>
                {agent?.languages?.map((l) => (
                  <div className={styles.sub} key={l} data-test="languages">
                    {l}
                  </div>
                ))}
              </>
            )}
            <strong>{t('Areas Served')}</strong>
            {skeleton && <Skeleton count={4} />}
            {agent?.activeLocations
              ?.reduce((acc, cur) => {
                const loc = `${cur.city}, ${cur.state}`;

                if (!~acc.findIndex((l) => l === loc)) {
                  acc.push(loc);
                }

                return acc;
              }, [] as string[])
              .map((l) => (
                <div className={styles.sub} key={l} data-test="area-served">
                  {l}
                </div>
              ))}
            <hr />
          </div>
          <div className={styles.bioContainer}>
            {(agent?.bio || skeleton) && <div className={styles.bio}>{skeleton ? <Skeleton count={6} /> : agent?.bio?.replaceAll(/<[^<]+?>/g, '')}</div>}
            {skeleton && <Skeleton count={2} />}
            {agent?.specializations && agent?.specializations.length > 0 && (
              <>
                <hr />
                <strong>{t('Specializations')}</strong>
                <div className={styles.specializationsContainer} data-test="specializations-container">
                  {agent?.specializations?.map((s) => (
                    <div className={styles.specialization} key={s}>
                      {s}
                    </div>
                  ))}
                </div>
                <hr />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfile;
